<template>
  <div class="flex flex-col min-h-screen pb-0">
    <!-- Intro Modal -->
    <div
      v-if="showIntroModal"
      class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-0 relative overflow-hidden mx-auto">
        <div class="flex transition-transform duration-300" :style="{ transform: `translateX(-${introPane * 100}%)` }">
          <!-- Pane 1 -->
          <div class="w-full max-w-md p-6 flex-shrink-0">
            <h2 class="text-xl font-bold mb-4">Welcome to Charmed!</h2>
            <p class="mb-6 text-gray-700">
              Charmed is a community-driven place for discovering local event, products and services from people near you.
            </p>
            <div class="flex justify-end">
              <button
                class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold"
                @click="nextPane"
              >
                Next
              </button>
            </div>
          </div>
          <!-- Pane 2 -->
          <div class="w-full max-w-md p-6 flex-shrink-0">
            <h2 class="text-xl font-bold mb-4">Discover & Earn</h2>
            <p class="mb-6 text-gray-700">
              Charmed's front page shows you nearby events, products and services. You can earn rewards by engaging with local businesses.
            </p>
            <div class="flex justify-end">
              <button
                class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold"
                @click="nextPane"
              >
                Next
              </button>
            </div>
          </div>
          <!-- Pane 3: Seller Benefits -->
          <div class="w-full max-w-md p-6 flex-shrink-0">
            <h2 class="text-xl font-bold mb-4">For Sellers & Venues</h2>
            <p class="mb-6 text-gray-700">
              Charmed helps sellers and venues reach local customers, promote events, and reward loyal fans. Easily create offers, manage redemptions, and grow your business with real community engagement.
            </p>
            <div class="flex justify-end">
              <button
                class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold"
                @click="nextPane"
              >
                Next
              </button>
            </div>
          </div>
          <!-- Pane 4: Only show if location is not allowed -->
          <div v-if="!locationAllowed" class="w-full max-w-md p-6 flex-shrink-0">
            <h2 class="text-xl font-bold mb-4">Ready to Start?</h2>
            <p class="mb-6 text-gray-700">
              Charmed needs access to your location to show you nearby events, products and services, only while you're using the site. We never share or sell your location data.
            </p>
            <div class="mb-4">
              <button
                class="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold mb-2"
                @click="requestLocation"
              >
                Allow Location Access
              </button>
              <div class="text-xs text-gray-500 text-center">
                We need your permission to show you what's nearby.
              </div>
            </div>
            <div class="flex items-center mb-4">
              <input
                id="dontShowAgain"
                type="checkbox"
                v-model="dontShowAgain"
                class="mr-2"
              />
              <label for="dontShowAgain" class="text-sm text-gray-700">Don't show again</label>
            </div>
            <button
              class="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-semibold"
              @click="closeIntroModal"
            >
              Get Started
            </button>
          </div>
        </div>
        <!-- Dots indicator -->
        <div class="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
          <button
            v-for="(dot, idx) in (locationAllowed ? 3 : 4)"
            :key="idx"
            class="w-2 h-2 rounded-full"
            :class="introPane === idx ? 'bg-green-600' : 'bg-gray-300'"
            @click="goToPane(idx)"
            aria-label="Go to step"
          ></button>
        </div>
      </div>
    </div>

    <!-- Desktop Navigation -->
    <nav class="hidden md:block bg-white shadow-sm">
      <div class="max-w-screen-sm mx-auto px-4 h-16">
        <div class="flex items-center justify-between h-full">
          <div class="flex space-x-8">
            <router-link
              v-for="(item, index) in navItems" 
              :key="index"
              :to="item.to"
              class="flex items-center px-3 py-2 text-gray-700 hover:text-green-600 transition-colors"
              :class="{ 'text-green-600': $route.path === item.to }"
            >
              <span class="material-icons-outlined text-xl mb-1">{{ item.icon }}</span>
              {{ item.text }}
            </router-link>
          </div>
          <div class="flex space-x-4"></div>
        </div>
      </div>
    </nav>

    <!-- Main Content -->
    <main class="min-h-screen pb-16 md:pb-0 pt-4">
      <div class="max-w-screen-sm mx-auto">
        <router-view />
      </div>
    </main>

    <!-- Mobile Navigation -->
    <nav class="fixed left-0 right-0 bottom-0 z-50 md:hidden bg-white shadow-lg border-t border-gray-100">
      <div class="grid grid-cols-5 h-12">
        <router-link
          v-for="(item, index) in navItems" 
          :key="index"
          :to="item.to"
          class="flex flex-col items-center justify-center text-gray-700 hover:text-green-600 transition-colors"
          :class="{ 'text-green-600': $route.path === item.to }"
        >
          <span class="material-icons-outlined text-xl mb-1">{{ item.icon }}</span>
        </router-link>
      </div>
    </nav>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { auth } from './auth/auth';
import { geo } from './geo/geo';


const showIntroModal = ref(false);
const dontShowAgain = ref(false);
const introPane = ref(0);
const locationAllowed = ref(false);
const router = useRouter();

function nextPane() {
  // If moving to the location pane and location is already allowed, skip and close modal
  if ((introPane.value === 2 && locationAllowed.value) || (introPane.value === 3)) {
    closeIntroModal();
    return;
  }
  if (introPane.value < (locationAllowed.value ? 2 : 3)) introPane.value++;
}
function goToPane(idx) {
  introPane.value = idx
}
function closeIntroModal() {
  showIntroModal.value = false;
  if (dontShowAgain.value) {
    localStorage.setItem('charmed_intro_seen', '1');
  }
}

async function checkLocation() {
  // Use the async hasAllowedLocation method from auth
  const allowed = await auth.hasAllowedLocation?.() ?? false;
  locationAllowed.value = !!allowed;
}

async function requestLocation() {
  console.log('Requesting location access...');
  try {
    const position = await geo.getLocation();
    console.log('getLocation response:', position);
    // Only close if actual location is present and not temp
    if (
      position &&
      position.latitude &&
      position.longitude &&
      !geo.location.useTempPosition
    ) {
      locationAllowed.value = true;
      closeIntroModal();
    } else {
      locationAllowed.value = false;
      router.push('/nolocation');
      closeIntroModal();
    }
  } catch (e) {
    locationAllowed.value = false;
    router.push('/nolocation');
  }
}

onMounted(async () => {
  await checkLocation();
  introPane.value = 0; // Always start on pane 1
  if (!localStorage.getItem('charmed_intro_seen')) {
    showIntroModal.value = true;
  }
  auth.handleRedirect();
});

const navItems = [
  { to: '/', icon: 'map', text: 'Nearby' },
  { to: '/cart', icon: 'shopping_bag', text: 'Cart' },
  { to: '/wallet', icon: 'star', text: 'Rewards' },
  { to: '/messages', icon: 'chat', text: 'Messages' },
  { to: '/settings', icon: 'menu', text: 'Settings' }
];
</script>

<style>
@import url('https://fonts.googleapis.com/icon?family=Material+Icons+Outlined');

:root {
  --card-height: 400px;
  --card-width: 280px;
}

body {
  @apply antialiased text-gray-800 bg-gray-50;
}

/* Ensure main content does not hide behind the fixed nav */
main {
  padding-bottom: 3.5rem !important; /* 56px = h-12 */
}
</style>